import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Search, X } from "lucide-react";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { TYPE_STYLE } from "@/components/SearchSuggest";
import { EntityLogo } from "@/components/entity/EntityLink";
import { entityTrack } from "@/lib/entities";

/* /entities — public front door to the NewsLayer Entity OS.
   Registry truth only: live entities, canonical routes, typed identity
   (Aave Protocol ≠ AAVE Token). Locked Entity Discovery UX v1 affordances.
   A future live flip (e.g. Lido) appears here with ZERO frontend changes. */

const TYPE_LABEL: Record<string, string> = {
  protocol: "PROTOCOLS", token: "TOKENS", chain: "CHAINS",
  company: "COMPANIES", project: "PROJECTS",
};

const fmtUsd = (v: number) =>
  v >= 1e9 ? `$${(v / 1e9).toFixed(2)}B`
    : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M`
      : `$${Math.round(v).toLocaleString()}`;

const fmtPrice = (v: number) =>
  v >= 1000 ? `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : v >= 1 ? `$${v.toFixed(2)}` : `$${v.toFixed(4)}`;

const EntityFact = ({ e }: { e: any }) => {
  if (e.type === "protocol" && typeof e.tvl_usd === "number") {
    return (
      <span className="flex items-baseline gap-1.5" data-testid={`explorer-fact-${e.type}-${e.slug}`}>
        <span className="font-mono text-[9px] font-bold tracking-[0.18em] text-gray-500">TVL</span>
        <span className="font-mono text-[13px] font-bold text-white">{fmtUsd(e.tvl_usd)}</span>
      </span>
    );
  }
  if (e.type === "token" && typeof e.price_usd === "number") {
    const chg = e.change_24h_pct;
    return (
      <span className="flex items-baseline gap-1.5" data-testid={`explorer-fact-${e.type}-${e.slug}`}>
        <span className="font-mono text-[13px] font-bold text-white">{fmtPrice(e.price_usd)}</span>
        {typeof chg === "number" && (
          <span className={`font-mono text-[10px] font-bold ${chg >= 0 ? "text-[#34D399]" : "text-[#F87171]"}`}>
            {chg >= 0 ? "+" : ""}{chg.toFixed(2)}% <span className="text-gray-600 font-normal">24H</span>
          </span>
        )}
      </span>
    );
  }
  return null; // missing ≠ zero — omit, never fabricate
};

const EntityCard = ({ e }: { e: any }) => (
  <Link to={e.route} data-testid={`explorer-entity-${e.type}-${e.slug}`}
    aria-label={`${e.name} — Entity Intelligence`}
    onClick={() => entityTrack("entity_nav_click", e.id, { surface: "entities_explorer" })}
    className="group relative flex md:flex-col items-center md:items-stretch gap-3 md:gap-0 rounded-xl border border-white/[0.10] bg-[#0b0c10] px-4 py-3 md:p-5 transition-[border-color,background-color] duration-200 hover:border-[#00E5FF]/50 hover:bg-[#0d1015] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]/60">
    <ArrowUpRight aria-hidden
      className="absolute right-3 top-3 h-3.5 w-3.5 text-[#00E5FF] opacity-50 md:opacity-0 transition-opacity duration-200 md:group-hover:opacity-100 md:group-focus-visible:opacity-100" />
    <span className="shrink-0 transition-transform duration-200 group-hover:scale-[1.05]">
      <EntityLogo type={e.type} slug={e.slug} name={e.name} size={34} />
    </span>
    <span className="min-w-0 flex-1 md:mt-3.5">
      <span className="flex items-baseline gap-2 min-w-0 pr-5 md:pr-0">
        <span className="font-heading font-bold text-[15px] leading-tight text-white truncate transition-colors duration-200 group-hover:text-[#00E5FF]">
          {e.name}
        </span>
        {e.symbol && <span className="font-mono text-[10.5px] text-gray-500 shrink-0">{e.symbol}</span>}
      </span>
      <span className={`inline-block mt-1.5 rounded border px-1.5 py-0.5 font-mono text-[7.5px] font-bold tracking-[0.16em] ${TYPE_STYLE[e.type] || TYPE_STYLE.project}`}>
        {String(e.type).toUpperCase()}{e.category ? ` · ${String(e.category).toUpperCase()}` : ""}
      </span>
    </span>
    <span className="shrink-0 md:mt-4 md:pt-3 md:border-t md:border-white/[0.07] md:w-full min-h-[1px]">
      <EntityFact e={e} />
    </span>
  </Link>
);

const SkeletonGrid = () => (
  <div data-testid="entities-loading" aria-hidden
    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="h-[62px] md:h-[168px] rounded-xl border border-white/[0.06] bg-white/[0.035] animate-pulse" />
    ))}
  </div>
);

export default function EntitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const type = searchParams.get("type") || "";
  const q = searchParams.get("q") || "";
  const [input, setInput] = useState(q);

  useEffect(() => { setInput(q); }, [q]);

  // URL-first state: debounce keystrokes into ?q= (replace — no history spam)
  useEffect(() => {
    const t = setTimeout(() => {
      const next = input.trim();
      if (next === q) return;
      const p = new URLSearchParams(searchParams);
      if (next) p.set("q", next); else p.delete("q");
      setSearchParams(p, { replace: true });
      if (next) entityTrack("entity_explorer_search", "entities_explorer", { q: next });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  // Base registry state (unfiltered): tab counts + total derive from live eligibility
  const base = useQuery({
    queryKey: ["entities-directory-base"],
    queryFn: async () => (await api.get("/entities/directory")).data,
    staleTime: 60_000,
  });
  const filtered = useQuery({
    queryKey: ["entities-directory", type, q],
    queryFn: async () => (await api.get("/entities/directory",
      { params: { type: type || undefined, q: q || undefined } })).data,
    staleTime: 30_000,
  });

  const tabs = useMemo(() => {
    const counts = base.data?.counts || {};
    const order = base.data?.types || [];
    // zero-live categories are hidden entirely — they appear automatically on first live flip
    return order.filter((t: string) => (counts[t] || 0) > 0);
  }, [base.data]);

  const setType = (t: string) => {
    const p = new URLSearchParams(searchParams);
    if (t) p.set("type", t); else p.delete("type");
    setSearchParams(p, { replace: true });
    entityTrack("entity_explorer_filter", "entities_explorer", { type: t || "all" });
  };

  const clearFilters = () => {
    setInput("");
    setSearchParams({}, { replace: true });
  };

  const entities = filtered.data?.entities || [];
  const total = base.data?.total;
  const groups = useMemo(() => {
    if (type) return [{ key: type, items: entities }];
    const by: Record<string, any[]> = {};
    for (const e of entities) (by[e.type] = by[e.type] || []).push(e);
    return Object.keys(by).map((k) => ({ key: k, items: by[k] }));
  }, [entities, type]);

  const jsonLd = useMemo(() => ([
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://newslayer.com/" },
        { "@type": "ListItem", position: 2, name: "Entities", item: "https://newslayer.com/entities" },
      ],
    },
    ...(base.data ? [{
      "@context": "https://schema.org", "@type": "ItemList",
      name: "NewsLayer Entity Explorer",
      numberOfItems: base.data.total,
      itemListElement: (base.data.entities || []).slice(0, 50).map((e: any, i: number) => ({
        "@type": "ListItem", position: i + 1, name: e.name,
        url: `https://newslayer.com${e.route}`,
      })),
    }] : []),
  ]), [base.data]);

  return (
    <div data-testid="entities-page" className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10 md:py-14">
      <Seo title="Entity Explorer — Canonical Onchain Intelligence Directory"
        description="Explore NewsLayer's intelligence graph of the onchain economy: canonical, typed protocol, token and chain entities with live coverage — Aave Protocol ≠ AAVE Token."
        path="/entities" jsonLd={jsonLd} />

      {/* ---- Header ---- */}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8 md:mb-10">
        <div className="max-w-2xl">
          <p className="font-mono text-[10px] font-bold tracking-[0.28em] text-[#00E5FF] mb-3">
            ENTITY INTELLIGENCE
          </p>
          <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl leading-[1.05] mb-3">
            Entity Explorer
          </h1>
          <p className="text-gray-400 text-sm md:text-base leading-relaxed">
            The canonical map of the onchain economy NewsLayer covers — typed identities,
            official marks, live intelligence routes. Protocols are not their tokens:
            Aave Protocol ≠ AAVE Token.
          </p>
        </div>
        {typeof total === "number" && (
          <div className="shrink-0 rounded-lg border border-white/[0.10] bg-[#0b0c10] px-4 py-3"
            data-testid="entities-total-count">
            <p className="font-mono text-[22px] font-bold text-white leading-none">{total}</p>
            <p className="font-mono text-[9px] tracking-[0.22em] text-gray-500 mt-1.5">LIVE ENTITIES</p>
          </div>
        )}
      </header>

      {/* ---- Controls ---- */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-5 mb-7">
        <div className="relative lg:w-[340px] shrink-0">
          <Search aria-hidden className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input value={input} onChange={(e) => setInput(e.target.value)}
            data-testid="entities-search-input" type="search" autoComplete="off"
            aria-label="Search entities by name, ticker or alias"
            placeholder="Search name, ticker or alias…"
            className="w-full rounded-lg border border-white/[0.12] bg-[#0b0c10] pl-9 pr-9 py-2.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#00E5FF]/60 focus:ring-1 focus:ring-[#00E5FF]/30 transition-colors" />
          {input && (
            <button onClick={() => setInput("")} aria-label="Clear search"
              data-testid="entities-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div role="tablist" aria-label="Entity type filter"
          className="flex gap-2 overflow-x-auto pb-1 lg:pb-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button role="tab" aria-selected={!type} onClick={() => setType("")}
            data-testid="entities-tab-all"
            className={`shrink-0 rounded-full border px-3.5 py-1.5 font-mono text-[10px] font-bold tracking-[0.14em] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]/60 ${!type
              ? "border-[#00E5FF]/60 text-[#00E5FF] bg-[#00E5FF]/[0.07]"
              : "border-white/[0.12] text-gray-400 hover:text-white hover:border-white/25"}`}>
            ALL{typeof total === "number" ? ` · ${total}` : ""}
          </button>
          {tabs.map((t: string) => (
            <button key={t} role="tab" aria-selected={type === t} onClick={() => setType(t)}
              data-testid={`entities-tab-${t}`}
              className={`shrink-0 rounded-full border px-3.5 py-1.5 font-mono text-[10px] font-bold tracking-[0.14em] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]/60 ${type === t
                ? "border-[#00E5FF]/60 text-[#00E5FF] bg-[#00E5FF]/[0.07]"
                : "border-white/[0.12] text-gray-400 hover:text-white hover:border-white/25"}`}>
              {TYPE_LABEL[t] || t.toUpperCase()} · {base.data?.counts?.[t]}
            </button>
          ))}
        </div>
      </div>

      {/* ---- Results ---- */}
      {filtered.isLoading || base.isLoading ? (
        <SkeletonGrid />
      ) : filtered.isError ? (
        <div data-testid="entities-error-state"
          className="rounded-xl border border-white/[0.10] bg-[#0b0c10] px-6 py-12 text-center">
          <p className="font-mono text-[10px] font-bold tracking-[0.24em] text-gray-500 mb-3">
            REGISTRY UNAVAILABLE
          </p>
          <p className="text-gray-400 text-sm mb-5">
            The entity registry could not be reached. The rest of NewsLayer is unaffected.
          </p>
          <button onClick={() => filtered.refetch()} data-testid="entities-retry-button"
            className="rounded-full border border-[#00E5FF]/50 px-5 py-2 font-mono text-[11px] font-bold tracking-[0.14em] text-[#00E5FF] hover:bg-[#00E5FF]/[0.08] transition-colors">
            TRY AGAIN
          </button>
        </div>
      ) : entities.length === 0 ? (
        <div data-testid="entities-empty-state"
          className="rounded-xl border border-white/[0.10] bg-[#0b0c10] px-6 py-12 text-center">
          <p className="font-mono text-[10px] font-bold tracking-[0.24em] text-gray-500 mb-3">
            NO MATCHES
          </p>
          <p className="text-gray-400 text-sm mb-5">
            No live {type ? (TYPE_LABEL[type] || type).toLowerCase() : "entities"}
            {q ? <> match <span className="text-white font-semibold">“{q}”</span></> : " available"}.
          </p>
          <button onClick={clearFilters} data-testid="entities-clear-filters"
            className="rounded-full border border-[#00E5FF]/50 px-5 py-2 font-mono text-[11px] font-bold tracking-[0.14em] text-[#00E5FF] hover:bg-[#00E5FF]/[0.08] transition-colors">
            CLEAR FILTERS
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.key} data-testid={`entities-group-${g.key}`}>
              {!type && groups.length > 1 && (
                <h2 className="font-mono text-[10px] font-bold tracking-[0.24em] text-gray-500 mb-3">
                  {TYPE_LABEL[g.key] || g.key.toUpperCase()}
                  <span className="text-gray-700"> · {g.items.length}</span>
                </h2>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                {g.items.map((e: any) => <EntityCard key={e.id} e={e} />)}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
